import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import React, { memo, useContext } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { RootState } from '../../../app/rootReducer';
import { useCaseLock } from '../../../common/caseLocking/CaseLockContext';
import { UserOrganization } from '../../../common/constants/userOrganization';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { ProductFactory } from '../../../common/productConfig/productFactory';
import { scrollToSection } from '../../../common/utilities';
import { useNotesBottomDrawerLayout } from '../../detail/notes/useNotesBottomDrawerLayout';
import detailStyles from '../../detail/styles.module.scss';
import { getCaseDetailNavTaskSegments } from '../../detail/utilities/evidenceTaskOrder';
import { isTaskViewable } from '../../detail/utilities/taskDetailUtilities';
import { AuthContext } from '../../login/AuthWrapper';
import { ITask } from '../../tasks/interfaces/ITask';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { taskUiActions } from '../../tasks/slice/taskUiSlice';
import {
  TaskTypes,
  TaskTypeToTaskTitleMapping,
} from '../../tasks/types/taskTypes';
import IconMapper from './icons/IconMapper';
import NavLink from './icons/NavLink';
import Referral from './icons/Referral';
import iconStyles from './icons/styles.module.scss';
import styles from './styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;
export const MemoizedIconLinks = memo(
  ({
    segments,
    showFamilyCoverage,
    setActive,
  }: {
    segments: ReturnType<typeof getCaseDetailNavTaskSegments>;
    showFamilyCoverage?: boolean;
    setActive?: (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  }) => {
    const activeTaskId = useSelector(state => state.taskUi.activeTaskId);
    const auth = useContext(AuthContext);
    const { flags } = useContext(FeatureFlagsContext);
    const renderStateful = () => {
      const items: React.ReactNode[] = [];

      const pushTaskIcon = (task: ITask) => {
        const linkId = task.id.toString();
        const iconMapper = isTaskViewable(
          auth.userOrganization,
          task.taskType,
        ) ? (
          <IconMapper
            key={linkId}
            dataLinkId={linkId}
            state={task.state}
            copy={TaskTypeToTaskTitleMapping[task.taskType]}
            isActive={linkId === activeTaskId}
            setActive={setActive}
          />
        ) : null;
        if (iconMapper) {
          items.push(iconMapper);
          if (task.taskType === TaskTypes.OVERVIEW && showFamilyCoverage) {
            items.push(
              <NavLink
                key="family-coverage-nav"
                dataLinkId="FamilyCoverage"
                icon={faUsers}
                iconClassName={iconStyles.Icon}
                iconContainerClassName={`${iconStyles.LeftNavIconLinkContainer} ${iconStyles.IconLinkContainer}`}
                textClassName={iconStyles.LinkRight}
                copy="Family Coverage"
                onClick={scrollToSection}
              />,
            );
          }
        }
      };

      segments.topDefaultTasks.forEach(pushTaskIcon);
      segments.trlAndDisclosureTasks.forEach(pushTaskIcon);
      if (segments.doctorInfoTask) {
        pushTaskIcon(segments.doctorInfoTask);
      }
      segments.rxAndMibTasks.forEach(pushTaskIcon);
      segments.otherEvidenceTasks.forEach(pushTaskIcon);
      if (
        segments.notesTask &&
        isTaskViewable(auth.userOrganization, segments.notesTask.taskType)
      ) {
        pushTaskIcon(segments.notesTask);
      }
      if (
        flags.isCaseReferralEnabled &&
        auth.userOrganization === UserOrganization.ETHOS
      ) {
        items.push(<Referral key="referral-nav" />);
      }
      return <>{items}</>;
    };
    return <>{renderStateful()}</>;
  },
);

/**
 * Renders the left sidebar with task icon links. Order: defaults → TRL+Disclosure → Doctor Info → Rx+MIB → other → Notes → Referral
 * (prefix + letters) → Order MIB Codebacks → Notes → Referral at the bottom
 * (matches Detail: both evidence blocks, then Notes/Referral, then Raw Evidence).
 */
export const LeftSidebar = ({
  familyCoverageHasContent = false,
}: {
  familyCoverageHasContent?: boolean;
} = {}) => {
  const { bannerMessage } = useCaseLock();
  const { visible: showNotesDrawer } = useNotesBottomDrawerLayout();
  const tasks = useSelector(state => getAllTasks(state.taskList));
  const segments = useSelector(state =>
    getCaseDetailNavTaskSegments(state.taskList),
  );
  const { carrier, product, subCategory } = useSelector(state => ({
    carrier: state.application.carrier,
    product: state.application.product,
    subCategory: state.application.subCategory,
  }));
  const dispatch = useDispatch();

  const handleSetActive = (
    ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    const key = ev.currentTarget.getAttribute('data-link-id');
    const activeTask = tasks.find(item => key === item.id.toString());
    if (activeTask) {
      dispatch(
        taskUiActions.updateActiveTask({
          activeTaskId: activeTask.id.toString(),
        }),
      );
    }
  };

  const supportsFamilyCoverageDetails =
    ProductFactory.getProductConfig(
      carrier,
      product,
      subCategory,
    )?.supportsFamilyCoverageDetails() ?? false;

  return (
    <div
      className={`${styles.Sidebar} ${styles.SidebarLeft} ${
        bannerMessage ? detailStyles.WithCaseLockBannerLeftNav : ''
      } ${showNotesDrawer ? detailStyles.WithNotesDrawerLeftNav : ''}`}
    >
      <MemoizedIconLinks
        segments={segments}
        showFamilyCoverage={supportsFamilyCoverageDetails && familyCoverageHasContent}
        setActive={handleSetActive}
      />
    </div>
  );
};
